import { useState } from "react";

import { type ReferencedNodeValue } from "@doitintl/cmp-models";
import { type SelectChangeEvent, Stack } from "@mui/material";
import { useFormikContext } from "formik";
import noop from "lodash/noop";

import { cloudflowTexts } from "../../../../../../assets/texts";
import { useToggle } from "../../../../../../utils/useToggle";
import { ReferencedFieldStandalone } from "../../../ApiActionParametersForm/parameters/wrappers/ReferencedField/ReferencedFieldStandalone";
import { type NodeWitOutputModel } from "../../../ApiActionParametersForm/parameters/wrappers/ReferencedField/useReferencedFieldContext";
import { useFieldCommonProps } from "../../../ApiActionParametersForm/useFieldCommonProps";
import { FieldSectionHeader } from "../../../Common/FieldSectionHeader";
import SwitchDataSourceDialog from "../../../FilterAction/SwitchDataSourceDialog";
import { type TransformationNodeFormValues } from "./TransformationNodeForm";

export const TransformationNodeDataSourceForm = ({
  referenceableNodes,
}: {
  referenceableNodes: NodeWitOutputModel[];
}) => {
  const [showDataSourceDialog, openDataSourceDialog, closeDataSourceDialog] = useToggle(false);
  const [confirmDataSourceChange, setConfirmDataSourceChange] = useState<() => void>(noop);
  const { getFieldProps, setValues } = useFormikContext<TransformationNodeFormValues>();

  const transformationTypeProps = getFieldProps("type");

  const referencedNodeFieldProps = getFieldProps("referencedNodeField");
  const referencedNodeFieldCommonProps = useFieldCommonProps(
    referencedNodeFieldProps,
    cloudflowTexts.REFERENCED_FIELD,
    true
  );

  function changeDataSourceGuard<T>(
    generateValues: (input: T) => TransformationNodeFormValues
  ): (event: SelectChangeEvent<T>) => void {
    return ({ target: { value } }) => {
      const valueSetter = () => {
        setValues(generateValues(value as T), false);
      };

      if (!transformationTypeProps.value) {
        valueSetter();
      } else {
        openDataSourceDialog();
        setConfirmDataSourceChange(() => valueSetter);
      }
    };
  }

  const onReferencedNodeFieldChange = changeDataSourceGuard((value?: ReferencedNodeValue) => ({
    type: null,
    referencedNodeField: value ?? {
      referencedField: [],
      referencedNodeId: "",
    },
    transformation: null,
  }));

  return (
    <Stack
      spacing={2}
      sx={{
        mb: 3,
      }}
    >
      <SwitchDataSourceDialog
        isDialogOpened={showDataSourceDialog}
        content="Changing your data source will remove any action configuration you have assigned. Do you want to change your data source?"
        handleClose={closeDataSourceDialog}
        handleConfirm={() => {
          confirmDataSourceChange();
          closeDataSourceDialog();
        }}
      />
      <FieldSectionHeader
        title={cloudflowTexts.SELECT_FIELD_TO_TRANSFORM}
        subtitle={cloudflowTexts.DATA_WILL_BE_PASSED_DOWN_THE_FLOW}
      />
      <ReferencedFieldStandalone
        {...referencedNodeFieldCommonProps}
        referenceableNodes={referenceableNodes}
        onChange={onReferencedNodeFieldChange}
      />
    </Stack>
  );
};

export default TransformationNodeDataSourceForm;
